import { autorun } from "mobx"
import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import { Event, EventNames, MessageStatus } from "~proto"
import { debugProto, deserialize, Proto, ProtoData, serialize } from "~types/proto"
import { debug } from "~utils/print"
import { globalToast } from "~utils"
import { isWebSocketDisconnectMessage } from "~utils/websocket-error"
import { RootStoreModel } from "~models/root-store"

// export const REPLY_TIMEOUT = "REPLY_TIMEOUT"
export const REPLY_TIMEOUT = "超时未响应, 请重试"

const FLAG_ACK = 1 // 请求
const FLAG_REPLY = 2 // 响应
const FLAG_ACKED = 4 // 这次请求结束
// const FLAG_REPLIED = 8
const ACK_TIMEOUT = 10000
const REQUEST_TIMEOUT = 10000
const DELAY = 10 // 5ms 延迟，避免短期内消息风暴

type ReplyQ = {
  [cid: string]: {
    resolve: (data) => void
    reject: (error: Error) => void
  }
}

type AckQ = {
  [cid: string]: {
    resolve: () => void
    reject: (err: Error) => void
  }
}

/**
 * 通道管理
 */
export const ChannelStoreModel = types
  .model("ChannelStore")
  .props({
    sendq: types.optional(types.array(types.frozen<ProtoData>()), []),
    recvq: types.optional(types.array(types.frozen<ProtoData>()), []),
  })
  .volatile(
    (): {
      ackq: AckQ
      replyq: ReplyQ
      /** 记录最近发送的事件名（sendq 可能在 ACKED 后被移除，但 ERROR 可能延迟到达） */
      recentById: Map<number, EventNames>
      recentTimerById: Map<number, number>
    } => ({
      ackq: {},
      replyq: {},
      recentById: new Map(),
      recentTimerById: new Map(),
    }),
  )
  .views(() => ({}))
  .actions((self) => ({
    // 处理请求协议确认机制
    onAck: (data: Proto) => {
      const f = self.ackq[data.id]
      if (f) {
        f.resolve()
        delete self.ackq[data.id]
      }
    },
    setSendQ: (data: ProtoData[]) => {
      self.sendq.replace(data)
    },

    clearRecvQ: () => {
      self.recvq.clear()
    },

    clearSendQ: () => {
      self.sendq.clear()
    },

    setRecvQ: (data: ProtoData[]) => {
      self.recvq.replace(data)
    },

    rejectPendingOnDisconnect: () => {
      const disconnectError = new Error("websocket disconnected")
      Object.values(self.replyq).forEach((handler) => {
        handler.reject(disconnectError)
      })
      self.replyq = {}
      Object.values(self.ackq).forEach((handler) => {
        handler.reject(disconnectError)
      })
      self.ackq = {}
    },

    removeMessageNewFromSendqByMessageID: (m_id: string) => {
      const sendq = self.sendq.filter((x: ProtoData) => {
        if (x[1] === EventNames.MESSAGE_NEW) {
          const { message_id } = x[2]
          return m_id !== message_id
        }
        return true
      })
      self.sendq.replace(sendq)
    },

    // 处理响应
    onReply: (msg: Proto) => {
      const cid = msg.id
      const data = msg.data
      const f = self.replyq[cid]

      if (f) {
        debugProto(msg, "<<<")
        const { error } = data || {}
        if (error) {
          f.reject(new Error(error.message))
        } else {
          f.resolve(data)
        }
        delete self.replyq[cid]
        return
      }

      // 仅打了 FLAG_ACK 的请求（如 MESSAGE_NEW）不会注册 replyq；服务端可能以同 id 的 REPLY
      // 返回 EventNames.ERROR(11) 且 data.error，需提示并结束 ACK 等待、标记发送失败
      const errorPayload = data?.error ?? (msg.name === EventNames.ERROR ? data : null)
      const errText =
        typeof errorPayload?.message === "string" && errorPayload.message.trim() !== ""
          ? errorPayload.message
          : errorPayload?.code != null
            ? `错误码 ${errorPayload.code}`
            : ""
      if (!errorPayload && msg.name !== EventNames.ERROR) return

      // 某些 ACK-only 的后台查询（如频道设置）失败不应打断用户流程；
      // 尤其当页面同时通过 REST 成功拿到数据时，WS 的“频道不存在”会造成误导性弹窗。
      const pending = self.sendq.find((x: ProtoData) => x[0]?.[2] === cid)
      const pendingName = pending?.[1] ?? self.recentById.get(cid)
      const shouldSilence =
        pendingName === EventNames.CHAT_SETTING_INFO &&
        (errText.includes("频道不存在") || errText.includes("聊天室不存在") || errorPayload?.code === 40000)

      if (!shouldSilence) {
        if (errText && !isWebSocketDisconnectMessage(errText)) {
          globalToast.show(errText)
        } else if (msg.name === EventNames.ERROR) {
          globalToast.show("操作失败")
        }
      }

      const ackHandler = self.ackq[cid]
      if (ackHandler?.reject) {
        ackHandler.reject(new Error(errText || "ERROR"))
        delete self.ackq[cid]
      }

      if (pending?.[1] === EventNames.MESSAGE_NEW && pending[2]?.message_id) {
        const message_id = pending[2].message_id as string
        const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        const t = messageStore.sendMessageTimeoutMessageIdMap[message_id]
        if (t) clearTimeout(t)
        delete messageStore.sendMessageTimeoutMessageIdMap[message_id]
        const m = messageStore.messages.get(message_id)
        m?.setStatus(MessageStatus.FAILED)
      }
      // 同 setSendQ：这里在 onReply action 内可直接 replace，避免 TS 误判 action 不存在
      self.sendq.replace(self.sendq.filter((x: ProtoData) => x[0][2] !== cid))
    },

    // 处理来自服务器的心跳协议
    onHeartBeat: (data: Proto) => {
      // console.log(data)
    },

    // send proto
    send: (
      msg: Proto,
      options?: {
        onData?: (data) => void
        onError?: (error: Error) => void
        immediately?: boolean
      },
    ) => {
      if (!msg.name) {
        // FIXME: 首次启动会混进来一个空消息
        debug("empty msg: ", msg)
        return
      }
      const { websocketStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { onData, onError, immediately } = options || {}
      __DEV__ && console.log("发送消息: ", msg)

      // 记录最近发送的事件名（ERROR 可能在 ACKED 后才回来，此时 sendq 已无 pending）
      self.recentById.set(msg.id, msg.name)
      const prevTimer = self.recentTimerById.get(msg.id)
      if (prevTimer) window.clearTimeout(prevTimer)
      const t = window.setTimeout(() => {
        self.recentById.delete(msg.id)
        self.recentTimerById.delete(msg.id)
      }, 15000)
      self.recentTimerById.set(msg.id, t)

      if (immediately) {
        const x = msg.unstructure()
        const [, flags, id] = x[0]
        debugProto({ id, flags, name: x[1], data: x[2] }, "immediately >>>")
        websocketStore.send(serialize(x))
      } else {
        self.sendq.push(msg.unstructure())
      }

      // request / reply
      if (msg.flags & FLAG_REPLY && onData) {
        self.replyq[msg.id] = createReplyCallback(msg.id, onData, onError)
      }
    },
  }))
  .actions((self) => {
    const { websocketStore, eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)

    // on data received from the server
    const ondata = (data: Uint8Array) => {
      const proto = deserialize(data)
      debugProto(proto, "<<<")
      if  (proto.name === EventNames.USER_SEARCH_LIST) {
        proto.name = EventNames.REQUEST_USER_SEARCH
        proto.flags = FLAG_REPLY
      }
      __DEV__ && console.log("响应消息: ", proto)
      if (proto.flags & FLAG_ACKED) {
        self.onAck(proto)
        return
      }

      if (proto.flags & FLAG_REPLY) {
        self.onReply(proto)
        return
      }

      if (proto.flags & FLAG_ACK) {
        const p = new Proto(proto.name, null, FLAG_ACKED, proto.id, proto.v)
        websocketStore.send(p.serialize())
      }

      // FIXME 需要注意短时间内消息过多的问题
      const evt = new Event(proto.name, proto.data, false)
      eventStore.onEvent(evt)
    }

    const createAckCallback = (id: number, x: ProtoData) => {
      const t = setTimeout(() => {
        delete self.ackq[id]
      }, ACK_TIMEOUT)
      return () => {
        clearTimeout(t)
        const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        if (x[1] === EventNames.MESSAGE_NEW) {
          const { message_id } = x[2]
          clearTimeout(messageStore.sendMessageTimeoutMessageIdMap[message_id])
        }
        self.setSendQ(self.sendq.filter((x) => x[0][2] !== id))
      }
    }

    const registerAckPromise = (
      id: number,
      resolve: (v?: any) => void,
      reject: (err: Error) => void,
      timeout = ACK_TIMEOUT,
    ) => {
      const t = setTimeout(() => {
        delete self.ackq[id]
        reject(new Error("ACK_TIMEOUT"))
      }, timeout)

      self.ackq[id] = {
        resolve: () => {
          clearTimeout(t)
          resolve(id)
        },
        reject: (err) => {
          clearTimeout(t)
          reject(err)
        },
      }
    }


    const flushSendQ = () => {
      // TODO: 批处理消息，避免惊群效应
      // 当 sendq 的长度或者 socket 的状态发生变化时候
      // 自动把当前队列里的消息投递出去

      if (self.sendq.length === 0 || !websocketStore.ready || !websocketStore.online) return
      debug(
        `[FlushSendQ] length: ${self.sendq.length}, sock ready: ${websocketStore.ready}, online: ${websocketStore.online}`,
      )

      const sendq = self.sendq.filter((x) => {
        const [, flags, id] = x[0]
        const ack = flags & FLAG_ACK
        if (ack) {
          // 回执队列中没有，表示该发送任务是新的或者是需要重发的
          // 创建回执任务
          if (!self.ackq[id]) {
            if (!self.ackq[id]) {
              registerAckPromise(
                id,
                () => {
                  const { messageStore } = getRoot<Instance<typeof RootStoreModel>>(self)
                  if (x[1] === EventNames.MESSAGE_NEW) {
                    const { message_id } = x[2]
                    clearTimeout(messageStore.sendMessageTimeoutMessageIdMap[message_id])
                  }
                  self.setSendQ(self.sendq.filter((x) => x[0][2] !== id))
                },
                () => {
                  // ACK timeout，可选择重发 or 标记失败
                },
              )
            }

          } else {
            // 还在等待回执, 跳过该发送任务
            return true
          }
        }
        debugProto({ id, flags, name: x[1], data: x[2] }, ">>>")
        websocketStore.send(serialize(x))

        // 移除不需要等待回执的任务
        return ack
      })

      if (sendq.length !== self.sendq.length) {
        self.setSendQ(sendq)
      }
    }

    // const flushRecvQ = () => {
    //   // 消息处理设计应该考虑幂等性原则
    //   debug(`[FlushRecvQ] length: ${self.recvq.length}`)
    //   if (self.recvq.length == 0) return
    //   self.recvq.forEach((x) => {
    //     const [, name, data] = x
    //     const evt = new Event(name, data, false)
    //     eventStore.onEvent(evt)
    //   })
    //   self.clearRecvQ()
    // }

    let disposers: any[] = []

    const afterCreate = () => {
      // TODO autorun, delay
      disposers = [autorun(flushSendQ, { delay: DELAY })]
    }

    const beforeDestory = () => {
      disposers.forEach((f) => f())
    }
    return { ondata, afterCreate, beforeDestory, registerAckPromise }
  })

function createReplyCallback(
  id: number,
  onData: (data) => void,
  onError?: (err: Error) => void,
  ts: number = REQUEST_TIMEOUT,
) {
  let done = false

  const t = setTimeout(() => {
    if (done) return
    done = true
    onError?.(new Error(REPLY_TIMEOUT))
  }, ts)

  const resolve = (event) => {
    if (done) return
    done = true
    clearTimeout(t)
    onData(event)
  }

  const reject = (error: Error) => {
    if (done) return
    done = true
    clearTimeout(t)
    onError?.(error)
  }

  return { resolve, reject }
}



type ChannelStoreType = Instance<typeof ChannelStoreModel>
export interface ChannelStore extends ChannelStoreType {}
type ChannelStoreSnapshotType = SnapshotOut<typeof ChannelStoreModel>
export interface ChannelStoreSnapshot extends ChannelStoreSnapshotType {}
export const createChannelStoreDefaultModel = () => types.optional(ChannelStoreModel, {})
